import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getDataForVehicleTrackingLoadCards,
  getLoadLineItemsForLoadHeader,
  getAttachedDeliveriesForVehicleTracking,
  updateVehicleTrackingLoad,
  getAttachedDeliveryLineItemsForVehicleTracking,
  getProducts,
  getCategoryBusinessPartnersDropdownOptions,
  confirmVehicleTrackingLoad,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { getPackType } from 'src/redux/Actions/VehicleTracking';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import 'src/style/createVehicleTracking.css';
import { Row, Col } from 'react-bootstrap';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';
import { ReactComponent as Create } from 'src/Images/icons/common/create.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';

const clampToZero = (value) => Math.max(0, Number(value) || 0);
const incrementValue = (currentValue) =>
  clampToZero((Number(currentValue) || 0) + 1);
const decrementValue = (currentValue) =>
  clampToZero((Number(currentValue) || 0) - 1);

// All delivery_line_item_ids currently present in the Load Line Items list.
const getCheckedLineItemIds = (loadLineItemsList) =>
  new Set(
    loadLineItemsList
      .filter((lineItemObj) => !lineItemObj.isNewLineItem)
      .flatMap((lineItemObj) =>
        (lineItemObj.line_items || []).map((li) => li.delivery_line_item_id),
      ),
  );

// A delivery group is locked for deletion if any of its line items are Confirmed.
const hasConfirmedLineItem = (lineItemObj) =>
  (lineItemObj?.line_items || []).some((li) => li?.status?.id === 2);

export default function VehicleTrackingLoadEditComponent({
  onBack,
  themeColor,
  vtLoadHeaderUniqueNumber,
}) {
  const { vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const canConfirmLoad = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CONFIRM_LOAD,
  );

  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { packTypeDataList } = vehicleTrackingReducer;

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const {
    vehicleTrackingLoadDetails,
    loadLineItemsForLoadHeader,
    attachedDeliveriesForVehicleTracking,
    attachedDeliveryLineItemsForVehicleTracking,
    products,
    categoryBasedBusinessPartner,
  } = VehicleTrackingOutwardReducer;

  const [loadLineItemsList, setLoadLineItemsList] = useState([]);
  const [attachedDeliveryList, setAttachedDeliveryList] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const [filterFormData, setFilterFormData] = useState({
    deliveryNumber: null,
  });
  const [lineItemErrors, setLineItemErrors] = useState({});
  const [selectedLoadLineItemIds, setSelectedLoadLineItemIds] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [businessPartnerSearchTerm, setBusinessPartnerSearchTerm] =
    useState('');

  const deliveryHeaderDropdownOptions = (
    attachedDeliveriesForVehicleTracking?.delivery || []
  ).map((deliveryObj) => ({
    value: deliveryObj.delivery_unique_number,
    label: `${deliveryObj.delivery_code ?? '-'} - ${deliveryObj.sap_code ?? '-'}`,
  }));

  const deliveryLineItemOptions = (
    attachedDeliveryLineItemsForVehicleTracking || []
  ).map((lineItem) => ({
    value: lineItem.delivery_unique_number,
    label: `${lineItem.delivery_code ?? '-'} - ${lineItem.sap_code ?? '-'}`,
    raw: lineItem,
  }));

  const productOptions = (products || []).map((product) => ({
    value: product.id,
    label: product.name,
  }));

  const businessPartnerOptions = (categoryBasedBusinessPartner || []).map(
    (partner) => ({
      value: partner.id,
      label: partner.name,
    }),
  );

  const packTypeOptions = (packTypeDataList || []).map((item) => ({
    label: item?.name,
    value: item?.id,
  }));

  useEffect(() => {
    if (
      !tenantOrganisationId ||
      !vehicleTrackingNumber ||
      !vtLoadHeaderUniqueNumber
    )
      return;

    dispatch(
      getDataForVehicleTrackingLoadCards(
        tenantOrganisationId,
        vehicleTrackingNumber,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }, [
    dispatch,
    tenantOrganisationId,
    vehicleTrackingNumber,
    vtLoadHeaderUniqueNumber,
  ]);

  useEffect(() => {
    if (!tenantOrganisationId || !vtLoadHeaderUniqueNumber) return;

    dispatch(
      getLoadLineItemsForLoadHeader(
        tenantOrganisationId,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }, [dispatch, tenantOrganisationId, vtLoadHeaderUniqueNumber]);
  // Fetch delivery line items based on the selected product and business partner.
  useEffect(() => {
    if (!tenantOrganisationId) return;
    dispatch(getProducts(tenantOrganisationId, productSearchTerm));
  }, [dispatch, tenantOrganisationId, productSearchTerm]);

  useEffect(() => {
    if (!tenantOrganisationId) return;
    dispatch(getPackType(tenantOrganisationId));
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (!tenantOrganisationId) return;
    dispatch(
      getCategoryBusinessPartnersDropdownOptions(
        tenantOrganisationId,
        businessPartnerSearchTerm,
        '1',
      ),
    );
  }, [tenantOrganisationId, dispatch, businessPartnerSearchTerm]);

  useEffect(() => {
    if (!tenantOrganisationId || !vehicleTrackingNumber) return;

    dispatch(
      getAttachedDeliveriesForVehicleTracking(
        tenantOrganisationId,
        vehicleTrackingNumber,
      ),
    );
  }, [dispatch, tenantOrganisationId, vehicleTrackingNumber]);

  useEffect(() => {
    setAttachedDeliveryList(
      attachedDeliveriesForVehicleTracking?.delivery || [],
    );
  }, [attachedDeliveriesForVehicleTracking]);

  // Seed "Load Line Items" purely from loadLineItemsForLoadHeader
  useEffect(() => {
    const loadLineItemsGroupedByDelivery = (lineItems = []) => {
      const deliveriesByKey = {};

      lineItems.forEach((li) => {
        const key = li.delivery_unique_number || `load-line-item-${li.id}`;
        let businessPartnerSelection = null,
          packTypeSelection = null,
          productSelection = null;

        if (li.ship_to_party) {
          businessPartnerSelection = {
            value: li.ship_to_party.id,
            label: li.ship_to_party.name,
          };
        }

        if (li.pack_type) {
          packTypeSelection = {
            label: li.pack_type,
            value: li.pack_type,
          };
        }

        if (li.product) {
          productSelection = {
            value: li.product.product_id,
            label: li.product.product_sap_code,
          };
        }

        if (!deliveriesByKey[key]) {
          deliveriesByKey[key] = {
            rowKey: key,
            delivery_id: li.delivery_id,
            delivery_code: li.delivery_code,
            delivery_unique_number: li.delivery_unique_number,
            sap_code: li.sap_code,
            delivery_date: li.delivery_date,
            sales_order_number: li.sales_order_number,
            ship_to_party: li.ship_to_party || null,
            businessPartnerSelection,
            deliverySelection: null,
            pack_type: packTypeSelection,
            pack_quantity: clampToZero(li.pack_quantity),
            line_items: [],
          };
        }

        deliveriesByKey[key].line_items.push({
          delivery_line_item_id: li.delivery_line_item_id,
          load_line_item_id: li.id,
          product: li.product,
          productSelection,
          product_description: li.product?.product_description,
          plant: li.plant,
          quantity: clampToZero(li.receiving_quantity),
          status: li.status || null,
        });
      });

      return Object.values(deliveriesByKey);
    };

    setLoadLineItemsList(
      loadLineItemsGroupedByDelivery(
        loadLineItemsForLoadHeader?.lineItems || [],
      ),
    );
    setSelectedLoadLineItemIds(
      (loadLineItemsForLoadHeader?.lineItems || [])
        .map((item) => item.id)
        .filter((id) => id !== null && id !== undefined),
    );
  }, [loadLineItemsForLoadHeader]);

  const selectableLoadLineItemIds = loadLineItemsList.flatMap((delivery) =>
    (delivery.line_items || [])
      .map((item) => item.load_line_item_id)
      .filter((id) => id !== null && id !== undefined),
  );
  const allLoadLineItemsSelected =
    selectableLoadLineItemIds.length > 0 &&
    selectableLoadLineItemIds.every((id) =>
      selectedLoadLineItemIds.includes(id),
    );
  const loadLineItems = loadLineItemsList.flatMap(
    (delivery) => delivery.line_items || [],
  );
  const allLoadLineItemsConfirmed =
    loadLineItems.length > 0 &&
    loadLineItems.every((item) => item.status?.id === 2);

  const handleSelectAllLoadLineItems = (checked) => {
    setSelectedLoadLineItemIds(checked ? selectableLoadLineItemIds : []);
  };

  const handleLoadLineItemSelection = (loadLineItemId, checked) => {
    setSelectedLoadLineItemIds((prev) =>
      checked
        ? prev.includes(loadLineItemId)
          ? prev
          : [...prev, loadLineItemId]
        : prev.filter((id) => id !== loadLineItemId),
    );
  };

  const handleConfirmLoad = () => {
    if (!canConfirmLoad || isConfirming) return;
    const selectedIds = selectableLoadLineItemIds.filter((id) =>
      selectedLoadLineItemIds.includes(id),
    );
    if (!selectedIds.length) return;

    setIsConfirming(true);
    dispatch(
      confirmVehicleTrackingLoad({
        tenant_organisation_id: tenantOrganisationId,
        vt_load_header_unique_number: vtLoadHeaderUniqueNumber,
        vehicle_tracking_load_line_item_ids: selectedIds,
      }),
    )
      .then(() => {
        dispatch(
          getDataForVehicleTrackingLoadCards(
            tenantOrganisationId,
            vehicleTrackingNumber,
            vtLoadHeaderUniqueNumber,
          ),
        );
        onBack?.();
      })
      .finally(() => setIsConfirming(false));
  };

  const handleDeliveryHeaderDropdownChange = (selectedOption) => {
    if (!selectedOption) {
      setFilterFormData({
        deliveryNumber: null,
      });
      setAttachedDeliveryList(
        attachedDeliveriesForVehicleTracking?.delivery || [],
      );
      return;
    }

    setFilterFormData({
      deliveryNumber: selectedOption,
    });
  };

  // Attached Deliveries checkbox: checked purely by comparing delivery_line_item_id
  // against what's currently in the Load Line Items list.
  const isLineItemSelected = (lineItemObj) => {
    const checkedLineItemIds = getCheckedLineItemIds(loadLineItemsList);
    return (lineItemObj?.line_items || []).some((li) =>
      checkedLineItemIds.has(li?.delivery_line_item_id),
    );
  };

  const handleToggleDeliverySelection = (lineItemObj, checked) => {
    if (!lineItemObj) return;

    const deliveryKey = lineItemObj.delivery_unique_number;
    if (!deliveryKey) return;

    setLoadLineItemsList((prev) => {
      if (checked) {
        const alreadyAdded = prev.some(
          (delivery) => delivery.delivery_unique_number === deliveryKey,
        );

        if (alreadyAdded) {
          return prev;
        }
        return [...prev, { ...lineItemObj, rowKey: deliveryKey }];
      }

      return prev.filter(
        (delivery) => delivery.delivery_unique_number !== deliveryKey,
      );
    });
  };

  const handleToggleAllDeliveries = (checked) => {
    const visibleDeliveryKeys = new Set(
      attachedDeliveryList
        .map((delivery) => delivery.delivery_unique_number)
        .filter(Boolean),
    );

    setLoadLineItemsList((prev) => {
      const existingVisibleDeliveries = prev.filter((delivery) =>
        visibleDeliveryKeys.has(delivery.delivery_unique_number),
      );
      const otherDeliveries = prev.filter(
        (delivery) => !visibleDeliveryKeys.has(delivery.delivery_unique_number),
      );

      if (!checked) return otherDeliveries;

      const existingDeliveryKeys = new Set(
        existingVisibleDeliveries.map(
          (delivery) => delivery.delivery_unique_number,
        ),
      );
      const newlySelectedDeliveries = attachedDeliveryList.filter(
        (delivery) =>
          delivery.delivery_unique_number &&
          !existingDeliveryKeys.has(delivery.delivery_unique_number),
      );
      return [
        ...otherDeliveries,
        ...existingVisibleDeliveries,
        ...newlySelectedDeliveries.map((delivery) => ({
          ...delivery,
          rowKey: delivery.delivery_unique_number,
        })),
      ];
    });
  };

  const handleRemoveLoadLineItem = (lineItemObj) => {
    if (!lineItemObj) return;

    if (hasConfirmedLineItem(lineItemObj)) return;

    const deliveryKey = lineItemObj.rowKey;
    if (!deliveryKey) return;

    if (lineItemObj.isNewLineItem) {
      setLineItemErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[deliveryKey];
        return updatedErrors;
      });
    }

    setLoadLineItemsList((prev) =>
      prev.filter((delivery) => delivery.rowKey !== deliveryKey),
    );
  };

  const handleSearchSubmit = () => {
    if (!filterFormData.deliveryNumber) {
      setAttachedDeliveryList(
        attachedDeliveriesForVehicleTracking?.delivery || [],
      );
      return;
    }

    const allDeliveries = attachedDeliveriesForVehicleTracking?.delivery || [];

    const filtered = allDeliveries.filter(
      (lineItemObj) =>
        lineItemObj?.delivery_unique_number ===
        filterFormData.deliveryNumber.value,
    );

    setAttachedDeliveryList(filtered);
  };

  const handleDeliveryDropdownOpen = (lineItemObj, item) => {
    dispatch(
      getAttachedDeliveryLineItemsForVehicleTracking(
        tenantOrganisationId,
        vehicleTrackingNumber,
        item?.productSelection?.value,
        lineItemObj?.businessPartnerSelection?.value,
      ),
    );
  };

  const handlePackTypeChange = (deliveryKey, selectedOption) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) =>
        lineItemObj.rowKey === deliveryKey
          ? { ...lineItemObj, pack_type: selectedOption }
          : lineItemObj,
      ),
    );
  };

  const handlePackQuantityChange = (deliveryKey, value) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) =>
        lineItemObj.rowKey === deliveryKey
          ? { ...lineItemObj, pack_quantity: clampToZero(value) }
          : lineItemObj,
      ),
    );
  };

  const handlePackQuantityIncrement = (deliveryKey) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        return {
          ...lineItemObj,
          pack_quantity: incrementValue(lineItemObj.pack_quantity),
        };
      }),
    );
  };

  const handlePackQuantityDecrement = (deliveryKey) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        return {
          ...lineItemObj,
          pack_quantity: decrementValue(lineItemObj.pack_quantity),
        };
      }),
    );
  };

  const handleDeliveryLineItemSelect = (deliveryKey, selectedOption) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        const rawLineItem = selectedOption?.raw;

        return {
          ...lineItemObj,
          deliverySelection: selectedOption,
          pack_quantity: clampToZero(rawLineItem?.pack_quantity),
          delivery_date:
            rawLineItem?.delivery_date ?? lineItemObj.delivery_date,
          line_items: (lineItemObj.line_items || []).map((item) => {
            if (item?.delivery_line_item_id) return item;

            return {
              ...item,
              quantity: clampToZero(rawLineItem?.quantity),
              plant: rawLineItem?.plant ?? item.plant,
            };
          }),
        };
      }),
    );
  };

  const clearLineItemFieldError = (deliveryKey, fieldName) => {
    setLineItemErrors((prev) => {
      if (!prev[deliveryKey]?.[fieldName]) return prev;

      return {
        ...prev,
        [deliveryKey]: {
          ...prev[deliveryKey],
          [fieldName]: false,
        },
      };
    });
  };

  const handleBusinessPartnerSelect = (deliveryKey, selectedOption) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) =>
        lineItemObj.rowKey === deliveryKey
          ? { ...lineItemObj, businessPartnerSelection: selectedOption }
          : lineItemObj,
      ),
    );

    if (selectedOption?.value) {
      clearLineItemFieldError(deliveryKey, 'businessPartner');
      clearLineItemFieldError(deliveryKey, 'product');
    }
  };

  const handleProductSelect = (deliveryKey, lineItemKey, selectedOption) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        return {
          ...lineItemObj,
          line_items: (lineItemObj.line_items || []).map((item) =>
            (item?.load_line_item_id ?? item?.new_line_item_id) === lineItemKey
              ? { ...item, productSelection: selectedOption }
              : item,
          ),
        };
      }),
    );

    if (selectedOption?.value) {
      clearLineItemFieldError(deliveryKey, 'product');
      clearLineItemFieldError(deliveryKey, 'businessPartner');
    }
  };

  const handleQuantityChange = (deliveryKey, lineItemId, value) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        return {
          ...lineItemObj,
          line_items: (lineItemObj.line_items || []).map((item) =>
            (item?.load_line_item_id ?? item?.new_line_item_id) === lineItemId
              ? { ...item, quantity: clampToZero(value) }
              : item,
          ),
        };
      }),
    );
  };

  const handleQuantityIncrement = (deliveryKey, lineItemId) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        return {
          ...lineItemObj,
          line_items: (lineItemObj.line_items || []).map((item) =>
            (item?.load_line_item_id ?? item?.new_line_item_id) === lineItemId
              ? { ...item, quantity: incrementValue(item?.quantity) }
              : item,
          ),
        };
      }),
    );
  };

  const handleQuantityDecrement = (deliveryKey, lineItemId) => {
    setLoadLineItemsList((prev) =>
      prev.map((lineItemObj) => {
        if (lineItemObj.rowKey !== deliveryKey) return lineItemObj;

        return {
          ...lineItemObj,
          line_items: (lineItemObj.line_items || []).map((item) =>
            (item?.load_line_item_id ?? item?.new_line_item_id) === lineItemId
              ? { ...item, quantity: decrementValue(item?.quantity) }
              : item,
          ),
        };
      }),
    );
  };

  const handleAddNewLineItem = () => {
    const rowKey = `new-${Date.now()}`;

    setLoadLineItemsList((prev) => [
      ...prev,
      {
        rowKey,
        isNewLineItem: true,
        deliverySelection: null,
        businessPartnerSelection: null,
        pack_type: null,
        pack_quantity: 0,
        line_items: [
          {
            new_line_item_id: rowKey,
            productSelection: null,
            quantity: 0,
          },
        ],
      },
    ]);
  };

  const buildLoadLineItemPayload = () =>
    loadLineItemsList.flatMap((lineItemObj) =>
      (lineItemObj.line_items || []).map((lineItem) => {
        const selectedDeliveryLineItemId =
          lineItemObj?.deliverySelection?.raw?.delivery_line_item_id;
        const selectedProductId =
          lineItem?.productSelection?.value || lineItem?.product?.product_id;
        const selectedBusinessPartnerId =
          lineItemObj?.businessPartnerSelection?.value ||
          lineItemObj?.ship_to_party?.id;
        const isSelectedPackType = (packTypeDataList || []).some(
          (packType) => packType.id === lineItemObj?.pack_type?.value,
        );

        const payloadLineItem = {
          delivery_line_item_id:
            lineItem?.delivery_line_item_id || selectedDeliveryLineItemId,
          receiving_quantity: lineItem?.quantity,
          pack_quantity: lineItemObj?.pack_quantity,
        };

        if (lineItem?.load_line_item_id && !lineItemObj?.isNewLineItem) {
          payloadLineItem.load_line_item_id = lineItem.load_line_item_id;
        }

        if (isSelectedPackType) {
          payloadLineItem.pack_type_id = lineItemObj.pack_type.value;
        }

        if (selectedProductId) {
          payloadLineItem.product_id = selectedProductId;
        }
        if (selectedBusinessPartnerId) {
          payloadLineItem.business_partner_id = selectedBusinessPartnerId;
        }

        if (!lineItem?.delivery_line_item_id) {
          delete payloadLineItem.delivery_line_item_id;

          if (selectedDeliveryLineItemId) {
            payloadLineItem.delivery_line_item_id = selectedDeliveryLineItemId;
          }
        }

        return payloadLineItem;
      }),
    );

  const validateNewLineItems = () => {
    const errors = {};

    loadLineItemsList.forEach((lineItemObj) => {
      if (!lineItemObj.isNewLineItem) return;

      const firstLineItem = lineItemObj.line_items?.[0] || {};
      const hasProduct = !!firstLineItem.productSelection?.value;
      const hasBusinessPartner = !!lineItemObj.businessPartnerSelection?.value;

      if (!hasProduct && !hasBusinessPartner) {
        errors[lineItemObj.rowKey] = {
          product: true,
          businessPartner: true,
        };
      }
    });

    setLineItemErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateNewLineItems()) {
      setActiveTab(1);
      return;
    }

    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      vehicle_tracking_number: vehicleTrackingNumber,
      vt_load_header_unique_number: vtLoadHeaderUniqueNumber,
      line_items: buildLoadLineItemPayload(),
    };

    dispatch(updateVehicleTrackingLoad(payload)).then(() => {
      dispatch(
        getLoadLineItemsForLoadHeader(
          tenantOrganisationId,
          vtLoadHeaderUniqueNumber,
        ),
      );
      dispatch(
        getDataForVehicleTrackingLoadCards(
          tenantOrganisationId,
          vehicleTrackingNumber,
          vtLoadHeaderUniqueNumber,
        ),
      );
      onBack?.();
    });
  };

  const getTabClassName = (tabKey) =>
    activeTab === tabKey
      ? `theme-pill-active-${themeColor}`
      : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey';

  const getNewLineItemSelectClassName = (deliveryKey, fieldName) =>
    `multi-select-input-section ${
      lineItemErrors[deliveryKey]?.[fieldName] ? 'form-input-error' : ''
    }`;

  const renderAttachedDeliveryLineItems = () => {
    if (!attachedDeliveryList?.length) {
      return (
        <tr>
          <td colSpan="10" className="text-center text-sm text-grey">
            No Delivery Attached Yet{' '}
          </td>
        </tr>
      );
    }

    return attachedDeliveryList.map((lineItemObj) => {
      const deliveryLineItems = lineItemObj.line_items || [];
      const tableRows = deliveryLineItems.length ? deliveryLineItems : [{}];
      const rowSpan = tableRows.length;
      const checked = isLineItemSelected(lineItemObj);

      return tableRows.map((item) => {
        const lineItemId = item?.delivery_line_item_id;

        return (
          <tr key={lineItemId || `${lineItemObj?.delivery_unique_number}`}>
            <td rowSpan={rowSpan}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) =>
                  handleToggleDeliverySelection(lineItemObj, e.target.checked)
                }
              />
            </td>

            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {lineItemObj?.sap_code ?? '-'}
              </span>
            </td>

            <td>
              <span className="text-sm text-nowrap">
                {item?.product?.product_sap_code ?? '-'}
              </span>
            </td>

            <td>
              <span className="text-sm text-nowrap table-ellipsis">
                {item?.product_description ||
                  item?.product?.product_description ||
                  '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap table-ellipsis">
                {lineItemObj?.ship_to_party?.name ?? '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {lineItemObj?.pack_type?.label ?? lineItemObj?.pack_type ?? '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {lineItemObj?.pack_quantity ?? 0}
              </span>
            </td>

            <td>
              <span className="text-sm text-nowrap">{item?.quantity ?? 0}</span>
            </td>

            <td>
              <span className="text-sm text-nowrap">
                {item?.plant?.name ?? '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {lineItemObj?.delivery_date ?? '-'}
              </span>
            </td>
          </tr>
        );
      });
    });
  };

  const renderLoadLineItems = () => {
    if (!loadLineItemsList?.length) {
      return (
        <tr>
          <td colSpan="12" className="text-center text-sm text-grey">
            No Load line items available.
          </td>
        </tr>
      );
    }

    return loadLineItemsList.map((lineItemObj) => {
      const deliveryLineItems = lineItemObj.line_items || [];
      const tableRows = deliveryLineItems.length ? deliveryLineItems : [{}];
      const rowSpan = tableRows.length;
      const deliveryKey = lineItemObj?.rowKey;
      const isDeleteDisabled = hasConfirmedLineItem(lineItemObj);

      const rowEditable = true;

      return tableRows.map((item) => {
        const lineItemId = item?.load_line_item_id ?? item?.new_line_item_id;
        const isDeliveryLineItemSelected = !item?.delivery_line_item_id;

        return (
          <tr key={lineItemId || `${lineItemObj?.delivery_unique_number}`}>
            <td>
              {item?.load_line_item_id ? (
                <input
                  type="checkbox"
                  aria-label="Select load line item"
                  checked={selectedLoadLineItemIds.includes(
                    item.load_line_item_id,
                  )}
                  onChange={(e) =>
                    handleLoadLineItemSelection(
                      item.load_line_item_id,
                      e.target.checked,
                    )
                  }
                />
              ) : null}
            </td>
            <td rowSpan={rowSpan}>
              {isDeliveryLineItemSelected ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="delivery_line_item"
                    className="multi-select-input-section"
                    classNamePrefix="react-select"
                    placeholder="Select Delivery Number"
                    options={deliveryLineItemOptions}
                    value={lineItemObj?.deliverySelection || null}
                    onMenuOpen={() =>
                      handleDeliveryDropdownOpen(lineItemObj, item)
                    }
                    onChange={(selectedOption) =>
                      handleDeliveryLineItemSelect(deliveryKey, selectedOption)
                    }
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap">
                  {lineItemObj?.sap_code ?? '-'}
                </span>
              )}
            </td>

            <td>
              {isDeliveryLineItemSelected ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="product"
                    className={getNewLineItemSelectClassName(
                      deliveryKey,
                      'product',
                    )}
                    classNamePrefix="react-select"
                    placeholder="Select Product"
                    options={productOptions}
                    value={item?.productSelection || null}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setProductSearchTerm(inputValue);
                      }
                    }}
                    filterOption={() => true}
                    onChange={(selectedOption) => {
                      setProductSearchTerm('');
                      handleProductSelect(
                        deliveryKey,
                        lineItemId,
                        selectedOption,
                      );
                    }}
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap">
                  {item?.product?.product_sap_code ?? '-'}
                </span>
              )}
            </td>

            <td>
              <span className="text-sm text-nowrap table-ellipsis">
                {item?.product_description ||
                  item?.product?.product_description ||
                  '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              {isDeliveryLineItemSelected ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="business_partner"
                    className={getNewLineItemSelectClassName(
                      deliveryKey,
                      'businessPartner',
                    )}
                    classNamePrefix="react-select"
                    placeholder="Select Business Partner"
                    options={businessPartnerOptions}
                    value={lineItemObj?.businessPartnerSelection || null}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setBusinessPartnerSearchTerm(inputValue);
                      }
                    }}
                    filterOption={() => true}
                    onChange={(selectedOption) => {
                      setBusinessPartnerSearchTerm('');
                      handleBusinessPartnerSelect(deliveryKey, selectedOption);
                    }}
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap table-ellipsis">
                  {lineItemObj?.ship_to_party?.name ?? '-'}
                </span>
              )}
            </td>

            <td rowSpan={rowSpan}>
              {rowEditable ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="pack_type"
                    className="multi-select-input-section"
                    classNamePrefix="react-select"
                    placeholder="Select Pack Type"
                    options={packTypeOptions}
                    value={lineItemObj?.pack_type || null}
                    onChange={(selectedOption) =>
                      handlePackTypeChange(deliveryKey, selectedOption)
                    }
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap">
                  {lineItemObj?.pack_type?.label ??
                    lineItemObj?.pack_type ??
                    '-'}
                </span>
              )}
            </td>

            <td rowSpan={rowSpan}>
              {rowEditable ? (
                <div className="input-wrapper">
                  <div className="form-input-section div-align-center div-flex-row div-space-between">
                    <div
                      role="button"
                      onClick={() => handlePackQuantityDecrement(deliveryKey)}
                      tabIndex={0}
                    >
                      <span className="text-lg text-semi-bold">-</span>
                    </div>
                    <input
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={lineItemObj?.pack_quantity ?? 0}
                      onChange={(e) =>
                        handlePackQuantityChange(
                          deliveryKey,
                          Number(e.target.value) || 0,
                        )
                      }
                      className="input-field-none text-sm text-semi-bold"
                    />
                    <div
                      role="button"
                      onClick={() => handlePackQuantityIncrement(deliveryKey)}
                      tabIndex={0}
                    >
                      <span className="text-lg text-semi-bold">+</span>
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-sm text-nowrap">
                  {lineItemObj?.pack_quantity ?? 0}
                </span>
              )}
            </td>

            <td>
              {rowEditable ? (
                <div className="input-wrapper">
                  <div className="form-input-section div-align-center div-flex-row div-space-between">
                    <div
                      role="button"
                      onClick={() =>
                        handleQuantityDecrement(deliveryKey, lineItemId)
                      }
                      tabIndex={0}
                    >
                      <span className="text-lg text-semi-bold">-</span>
                    </div>
                    <input
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={item?.quantity ?? 0}
                      onChange={(e) =>
                        handleQuantityChange(
                          deliveryKey,
                          lineItemId,
                          Number(e.target.value) || 0,
                        )
                      }
                      className="input-field-none text-sm text-semi-bold"
                    />
                    <div
                      role="button"
                      onClick={() =>
                        handleQuantityIncrement(deliveryKey, lineItemId)
                      }
                      tabIndex={0}
                    >
                      <span className="text-lg text-semi-bold">+</span>
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-sm text-nowrap">
                  {item?.quantity ?? 0}
                </span>
              )}
            </td>

            <td>
              <span className="text-sm text-nowrap">
                {item?.plant?.name ?? '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {lineItemObj?.delivery_date ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {item?.status?.name ?? '-'}
              </span>
            </td>

            <td rowSpan={rowSpan}>
              <div
                className={`div-flex-column div-align-center ${
                  isDeleteDisabled ? 'icon-disabled' : ''
                }`}
                role="button"
                tabIndex={0}
                aria-label="Remove line item"
                onClick={() => handleRemoveLoadLineItem(lineItemObj)}
              >
                <Delete className="icon-size-sm" />
              </div>
            </td>
          </tr>
        );
      });
    });
  };

  return (
    <div className="div-flex-column rg-10 h-100 padding-top-10">
      {/* Header */}
      <div className="div-flex-row div-space-between div-align-center">
        <div className="div-flex-row cg-20 div-align-center">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-bold">Edit Load</span>
          </div>
          <span className="light-blue-pill text-md">
            {vehicleTrackingLoadDetails?.loadCode}
          </span>
        </div>
      </div>
      <div className="div-flex-row div-space-between div-align-center padding-bottom-10">
        <div className="div-flex-row cg-10 text-sm">
          <button
            type="button"
            className={getTabClassName(1)}
            onClick={() => setActiveTab(1)}
          >
            Load Line Items
          </button>
          <button
            type="button"
            className={getTabClassName(2)}
            onClick={() => setActiveTab(2)}
          >
            Attached Deliveries
          </button>
        </div>
        <div className="div-flex-row cg-10 div-align-center">
          <button
            data-testid="create-vehicle-btn"
            className={`common-btn theme-background-${themeColor}`}
            type="button"
            onClick={handleSubmit}
          >
            Submit
          </button>
          {activeTab === 1 && !allLoadLineItemsConfirmed && (
            <button
              type="button"
              className={`common-btn theme-background-${themeColor}`}
              onClick={handleConfirmLoad}
              disabled={
                !canConfirmLoad ||
                !selectedLoadLineItemIds.some((id) =>
                  selectableLoadLineItemIds.includes(id),
                ) ||
                isConfirming
              }
            >
              {isConfirming ? 'Confirming...' : 'Confirm Load'}
            </button>
          )}
        </div>
      </div>
      {activeTab === 2 && (
        <>
          <Row className="w-100 div-space-between">
            <Col xxl={4} xl={4} lg={4} md={6} sm={12}>
              <div className="div-flex-column">
                <span className="text-sm text-bold">Delivery Number</span>
                <div className="input-wrapper delivery-number-select">
                  <CustomMultiSelect
                    name="delivery-number"
                    isMulti={false}
                    className="multi-select-input-section margin-top-5"
                    classNamePrefix="react-select"
                    placeholder="Search Delivery Number"
                    options={deliveryHeaderDropdownOptions}
                    value={filterFormData.deliveryNumber || ''}
                    onChange={handleDeliveryHeaderDropdownChange}
                    isClearable
                  />

                  <span className="form-input-section-icon">
                    <Search className="icon-size-sm" color="#9F9F9F" />
                  </span>
                </div>
              </div>
            </Col>

            <Col
              xxl={4}
              xl={4}
              lg={4}
              md={6}
              sm={12}
              className="d-flex justify-content-end align-items-end"
            >
              <div className="delivery-search-actions">
                <button
                  type="button"
                  className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                  onClick={handleSearchSubmit}
                >
                  Search
                </button>
              </div>
            </Col>
          </Row>

          <div className="div-flex-column rg-20 right-padding-10">
            <div className="purchase-order-line-item-card border-radius-4">
              <div className="div-space-between div-align-center px-2 py-2 border-bottom">
                <span className="text-md text-bold delivery-header">
                  Attached Deliveries
                </span>
              </div>
              <div className="load-item-table-wrapper">
                <table className="load-item-table">
                  <thead>
                    <tr>
                      <th className="table-head-40">
                        <input
                          type="checkbox"
                          aria-label="Select all deliveries"
                          checked={
                            attachedDeliveryList.length > 0 &&
                            attachedDeliveryList.every(isLineItemSelected)
                          }
                          onChange={(e) =>
                            handleToggleAllDeliveries(e.target.checked)
                          }
                        />
                      </th>
                      <th className="table-head-120">
                        <span className="text-sm text-grey text-semi-bold">
                          Delivery Number
                        </span>
                      </th>
                      <th className="table-head-150">
                        <span className="text-sm text-grey text-semi-bold">
                          Product Code
                        </span>
                      </th>
                      <th className="table-head-200">
                        <span className="text-sm text-grey text-semi-bold">
                          Product Description
                        </span>
                      </th>
                      <th className="table-head-200">
                        <span className="text-sm text-grey text-semi-bold">
                          Business Partner
                        </span>
                      </th>
                      <th className="table-head-150">
                        <span className="text-sm text-grey text-semi-bold">
                          Pack Type
                        </span>
                      </th>
                      <th className="table-head-120">
                        <span className="text-sm text-grey text-semi-bold">
                          Pack Quantity
                        </span>
                      </th>
                      <th className="table-head-120">
                        <span className="text-sm text-grey text-semi-bold">
                          Quantity
                        </span>
                      </th>
                      <th className="table-head-60">
                        <span className="text-sm text-grey text-semi-bold">
                          Plant
                        </span>
                      </th>
                      <th className="table-head-100">
                        <span className="text-sm text-grey text-semi-bold">
                          Delivery Date
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>{renderAttachedDeliveryLineItems()}</tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {activeTab === 1 && (
        <div className="div-flex-column rg-20 right-padding-10">
          <div className="purchase-order-line-item-card border-radius-4">
            <div className="div-space-between div-align-center px-2 py-2 border-bottom">
              <span className="text-md text-bold delivery-header">
                Load Line Items
              </span>
            </div>
            <div className="load-item-table-wrapper">
              <table className="load-item-table">
                <thead>
                  <tr>
                    <th className="table-head-40">
                      <input
                        type="checkbox"
                        aria-label="Select all load line items"
                        checked={allLoadLineItemsSelected}
                        onChange={(e) =>
                          handleSelectAllLoadLineItems(e.target.checked)
                        }
                      />
                    </th>
                    <th className="table-head-200">
                      <span className="text-sm text-grey text-semi-bold">
                        Delivery Number
                      </span>
                    </th>
                    <th className="table-head-200">
                      <span className="text-sm text-grey text-semi-bold">
                        Product Code
                      </span>
                    </th>
                    <th className="table-head-200">
                      <span className="text-sm text-grey text-semi-bold">
                        Product Description
                      </span>
                    </th>
                    <th className="table-head-200">
                      <span className="text-sm text-grey text-semi-bold">
                        Business Partner
                      </span>
                    </th>
                    <th className="table-head-150">
                      <span className="text-sm text-grey text-semi-bold">
                        Pack Type
                      </span>
                    </th>
                    <th className="table-head-120">
                      <span className="text-sm text-grey text-semi-bold">
                        Pack Quantity
                      </span>
                    </th>
                    <th className="table-head-120">
                      <span className="text-sm text-grey text-semi-bold">
                        Quantity
                      </span>
                    </th>
                    <th className="table-head-60">
                      <span className="text-sm text-grey text-semi-bold">
                        Plant
                      </span>
                    </th>
                    <th className="table-head-100">
                      <span className="text-sm text-grey text-semi-bold">
                        Delivery Date
                      </span>
                    </th>
                    <th className="table-head-100">
                      <span className="text-sm text-grey text-semi-bold">
                        Status
                      </span>
                    </th>
                    <th className="table-head-60">
                      <span className="text-sm text-grey text-semi-bold">
                        Action
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderLoadLineItems()}</tbody>
              </table>
            </div>
          </div>

          <div className="div-flex-row div-flex-end">
            <button
              type="button"
              onClick={handleAddNewLineItem}
              className={`common-btn-outlined theme-border-color-${themeColor}`}
            >
              <div className="div-flex-row cg-10 div-align-center">
                <Create
                  className="icon-size-sm"
                  color={getColorCode(themeColor)}
                />
                <span className="text-sm">Add Row</span>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
